import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { DateQuickLinkAddControl } from "@/Common/Components/Buttons/DateQuickLinkAddControl";
import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { Guid } from "@/Common/Models/Guid";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LISRISCode } from "@/Common/Models/LISRISCode";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { LabRequestBillCode, LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { ConsultationRequestPriorityOption, ExamRequestPriorityOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { MedicalRequestCommentsDisplay } from "./MedicalRequestCommentsDisplay";
import { MedicalRequestFormRecipientField } from "./MedicalRequestFormRecipientField";
import { MedicalRequestFormRowComponent } from "./MedicalRequestFormRow";
import { MedicalRequestViewComponent } from "./MedicalRequestViewComponent";

import stylesImport from "./MedicalRequestPopupController.module.scss";
import legacyStylesImport from "./MedicalRequestPopupControllerLegacy.module.scss";

interface IMedicalRequestFormComponentProps {
    model: IMedicalRequest;

    // use for christus
    siteId?: string;
    costCenterId?: string;

    disabled?: boolean;

    onRequestingStaffChange?: (contactFacetId: Guid) => void;
    onSpecialityChange?: (text: string) => void;
    onRecipientChange?: (recipient: string) => void;
    onRecipientSearchTextChange?: (searchText: string) => void;
    onPriorityChange?: (priorityDropDownId: string) => void;
    onReasonChange: (text: string) => void;
    onDescriptionChange: (text: string) => void;
    onBillCodesChange?: (billCode: LabRequestBillCode[]) => void;
    onCostCenterChange?: (id: string) => void;
    onDueDateChange?: (date: DateTimeWithPrecision) => void;
    isReasonClickableTextPanelOpen: boolean;
    isDescriptionClickableTextPanelOpen: boolean;
    setReasonClickableTextPanelVisibility: (value: boolean) => void;
    setDescriptionClickableTextPanelVisibility: (value: boolean) => void;
    onCloseAllClickableTextPanel: () => void;
    attachDocumentComponent: JSX.Element;
    type: MedicalRequestType;
    onContactDirectoryEntityChanged: (selection: DirectorySelectableEntity<IDirectoryEntity>) => void;
    onClearRecipient: () => void;
}

class MedicalRequestFormComponentState {
    public allLisRisCode: LISRISCode[];
    public currentFile: ExportableFileModel;

    public constructor(init?: Partial<MedicalRequestFormComponentState>) {
        Object.assign(this, init);
    }

    public static default(): MedicalRequestFormComponentState {
        return new MedicalRequestFormComponentState({
            allLisRisCode: [],
            currentFile: null
        });
    }
}

class MedicalRequestFormComponent extends React.Component<IMedicalRequestFormComponentProps, MedicalRequestFormComponentState> {
    private readonly styles = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
        ? stylesImport
        : legacyStylesImport;

    public constructor(props: IMedicalRequestFormComponentProps) {
        super(props);
        this.state = MedicalRequestFormComponentState.default();
    }

    public async componentDidMount(): Promise<void> {
        if (cacheStore.IsChristus()) {
            const codes = await LISRISCode.GetAll();
            this.mutateState(s => s.allLisRisCode = codes);
        }
    }

    private mutateState(mutation: (s: MedicalRequestFormComponentState) => void, then: () => void = null): void {
        const s = new MedicalRequestFormComponentState();
        mutation(s);
        this.setState(s, then);
    }

    public render(): JSX.Element {
        const model = this.props.model;
        if (!model)
            return null;

        if (this.props.disabled) {
            return (
                <MedicalRequestViewComponent
                    model={model}
                    disabled={true}
                    attachDocumentComponent={this.props.attachDocumentComponent}
                />
            );
        }
        return (
            <>
                <MedicalRequestFormRowComponent label="RequestingStaffLabel" dataCy="RequestingStaffRow" className={this.styles.requestStaffRow}>
                    {this.renderRequestingStaff(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="PriorityLabel" dataCy="PriorityRow" className={this.styles.priorityRow}>
                    {this.renderPriority(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="ResultsExpectedOnLabel" dataCy="ResultsExpectedOnRow" className={this.styles.resultsExpectedOnRow} shouldHide={this.shouldHideResultsExpectedOnRow}>
                    {this.renderDueDate(model.dueDate, model.dueDateText)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="BillingCodeLabel" dataCy="BillCodesRow" className={this.styles.billingCodeRowForm} shouldHide={!(cacheStore.IsChristus() && this.hasLabRequestType)}>
                    {this.renderBillingCodes(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="SpecialityLabel" dataCy="SpecialtyRow" className={this.styles.specialtyRow} shouldHide={!this.isConsultationRequest}>
                    {this.renderSpeciality(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="RecipientLabel" dataCy="RecipientRow" className={this.styles.recipientRow} shouldHide={!this.isConsultationRequest || cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements}>
                    {this.renderRecipient()}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="ReasonLabel" dataCy="ReasonRow" className={this.styles.reasonRow}>
                    {this.renderReasonClickableText(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="DescriptionLabel" dataCy="DescriptionRow" className={this.styles.descriptionRow}>
                    {this.renderDescriptionClickableText(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="AttachmentsLabel" dataCy="AttachmentsRow" className={`${this.styles.attachmentsRow} ${this.styles.formAttachments}`} shouldHide={!cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments}>
                    {this.props.attachDocumentComponent}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="CommentsLabel" dataCy="CommentsRow" className={this.styles.commentsRow} shouldHide={isArrayNullOrUndefinedOrEmpty(model.comments) || cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements}>
                    <MedicalRequestCommentsDisplay comments={model.comments} />
                </MedicalRequestFormRowComponent>
            </>
        );
    }

    private get shouldHideResultsExpectedOnRow(): boolean {
        // As part of the consultation request improvement project, this field has been moved
        // and needs to hide in this context when the feature flag is on only for consultation requests.
        return !this.isLabRequest && cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements;
    }

    private renderRequestingStaff(model: IMedicalRequest): JSX.Element {
        if (!this.props.disabled && !model.requestingStaffContactFacetId) {
            return (
                <TextboxControl
                    value=""
                    valid={this.isValid(model.requestingStaffContactFacetId)}
                    autoCompleteItems={this.employeeList}
                    onAutoCompleteSelectCallBack={(_: any, model: Person) =>
                        this.props.onRequestingStaffChange && this.props.onRequestingStaffChange(model.ContactFacetId)
                    }
                    onAutoCompleteSelectClearTextBox={true}
                    placeholder=""
                />
            );
        }

        const staff = cacheStore.GetCliniciansPersonsByContactFacetId(model.requestingStaffContactFacetId);

        return (
            <div>
                <span style={{ marginRight: "8px" }}>{staff.NameWithPrefix}</span>
                {!this.props.disabled && (
                    <span
                        style={{ color: "red", cursor: "pointer" }}
                        onClick={() => this.props.onRequestingStaffChange && this.props.onRequestingStaffChange(null)}
                    >
                        X
                    </span>
                )}
            </div>
        );
    }

    private renderSpeciality(model: IMedicalRequest): JSX.Element {
        const consultationModel = model as ConsultationRequest;

        const availableSpecialities = cacheStore.FindLookupsByTypeCode("SPECIALTYPERMITCODE")
            .map(x => new SearchableItemModelT<FlatLookupView>(x.GetValue(), x.LookupID, x))
            .toImmutable();

        return (
            <TextboxControl
                id="consultationRequestSpecialities"
                value={consultationModel.speciality}
                valid={this.isValid(consultationModel.speciality)}
                autoCompleteItems={availableSpecialities}
                onChange={value => this.handleOnSpeciatyChanged(value)}
                onAutoCompleteSelectCallBack={(code, model, value) => this.handleOnSpeciatyChanged(value)}
                onAutoCompleteSelectClearTextBox={false}
                placeholder=""
                disabled={this.props.disabled}
                dataCy="medicalRequestForm-specialities"
            />
        );
    }

    private renderRecipient(): JSX.Element {
        return (
            <MedicalRequestFormRecipientField
                model={this.props.model}
                disabled={this.props.disabled}
                onRecipientChange={(selection: string) => this.props.onRecipientChange?.(selection)}
                onRecipientSearchTextChange={(textValue: string) => this.props.onRecipientSearchTextChange?.(textValue)}
                onContactDirectoryEntityChanged={this.props.onContactDirectoryEntityChanged}
                onClearRecipient={this.props.onClearRecipient}
            />
        );
    }

    private renderPriority(model: IMedicalRequest): JSX.Element {
        let allComboBox = [];
        let urgentId = "";

        if (this.isConsultationRequest) {
            allComboBox = ConsultationRequestPriorityOption.getAllAsCombobox();
            urgentId = ConsultationRequestPriorityOption.Urgent.model.Id;
        }
        else {
            allComboBox = ExamRequestPriorityOption.getAllAsCombobox();
            urgentId = ExamRequestPriorityOption.Urgent.model.Id;
        }

        return (
            <div className="row">
                <div className="col-xs-6">
                    <SelectLookupControl
                        IncludeEmptyChoice={true}
                        invalid={!this.isValid(model.priorityDropDownId)}
                        Value={model.priorityDropDownId}
                        AvailableValues={allComboBox}
                        Callback={(value: string) => this.props.onPriorityChange && this.props.onPriorityChange(value)}
                        disabled={this.props.disabled}
                    />
                </div>
                {!this.props.disabled && this.props.model.priorityDropDownId != urgentId && (
                    <div className="col-xs-6" style={{ paddingTop: "8px", paddingLeft: "0" }}>
                        <a
                            style={{ color: "red" }}
                            onClick={() => this.props.onPriorityChange && this.props.onPriorityChange(urgentId)}
                        >
                            {cacheStore.GetString("UrgentLabel")}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    private renderReasonClickableText(model: IMedicalRequest): JSX.Element {
        const id = "request_edit_reason";
        const isReasonValid = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements && this.isConsultationRequest
            ? this.isValid(model.reason) || this.isValid(model.description)
            : true;

        return (
            <>
                <TextboxControl
                    id={id}
                    value={model.reason}
                    onChange={(value: string) => this.props.onReasonChange(value)}
                    valid={isReasonValid}
                    disabled={this.props.disabled}
                    style={{ height: "37px" }}
                />
                <FixedClickableTextPanel
                    isClickableTextPanelOpen={this.props.isReasonClickableTextPanelOpen}
                    onClickableTextPanelOpen={() => {
                        this.props.onCloseAllClickableTextPanel();
                        this.props.setReasonClickableTextPanelVisibility(true);
                    }}
                    onClickableTextPanelClose={() => this.props.onCloseAllClickableTextPanel()}
                    context={this.props.type === MedicalRequestType.LabRequest ? "RFRQ" : "RFCR"}
                    inputElementId={id}
                    cssClass="text-helper-container"
                    isLinkDisabled={this.props.disabled}
                    hasResponsiveHeight
                    linkClassName={this.styles.clickableTextLink}
                />
            </>
        );
    }

    private renderDescriptionClickableText(model: IMedicalRequest): JSX.Element {
        const id = "request_description_field";
        const isDescriptionInvalid = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements && this.isConsultationRequest
            ? !this.isValid(model.reason) && !this.isValid(model.description)
            : !this.isValid(model.description);

        return (
            <>
                <TextAreaAutoSize
                    id={id}
                    onTextChangeCallback={(value: string) => this.props.onDescriptionChange(value)}
                    value={model.description}
                    isInvalid={isDescriptionInvalid}
                    disabled={this.props.disabled}
                    maxHeight="150px"
                    dataCy="medicalRequestForm-description"
                />
                <FixedClickableTextPanel
                    isClickableTextPanelOpen={this.props.isDescriptionClickableTextPanelOpen}
                    onClickableTextPanelOpen={() => {
                        this.props.onCloseAllClickableTextPanel();
                        this.props.setDescriptionClickableTextPanelVisibility(true);
                    }}
                    onClickableTextPanelClose={() => this.props.onCloseAllClickableTextPanel()}
                    context={this.props.type === MedicalRequestType.LabRequest ? "REQ" : "CONS"}
                    inputElementId={id}
                    cssClass="text-helper-container"
                    isLinkDisabled={this.props.disabled}
                    hasResponsiveHeight
                    linkClassName={this.styles.clickableTextLink}
                />
            </>
        );
    }

    private renderBillingCodes(model: IMedicalRequest): JSX.Element {
        const labRequest = model as LabRequest;

        return (
            <div style={{ width: "100%" }}>
                <div className="row">
                    <div className="col-xs-8">
                        <TextboxControl
                            value=""
                            autoCompleteItems={this.getFilteredLisRisCacheList(labRequest.type, this.props.costCenterId)}
                            onAutoCompleteSelectCallBack={(code, model) => this.onBillCodeAdded(model)}
                            disabled={this.props.disabled}
                        />
                    </div>

                    <div className="col-xs-4">
                        <Combobox
                            HasEmptyItem={true}
                            id="_delayComboBox"
                            items={this.getCostCenterOptions(labRequest.type, this.props.siteId)}
                            selectedCode={this.props.costCenterId}
                            onChange={value => this.props.onCostCenterChange && this.props.onCostCenterChange(value)}
                            doInitialChangeOp={false}
                            style={{ height: "39px" }}
                            disabled={this.props.disabled}
                        />
                    </div>
                </div>

                {!this.props.disabled && labRequest.billCodes?.length > 0 && (
                    <div className="row" style={{ marginTop: "10px" }}>
                        {this.renderSelectedCodes(labRequest.billCodes)}
                    </div>
                )}
            </div>
        );
    }

    private renderSelectedCodes(billCodes: LabRequestBillCode[]): JSX.Element[] {
        if (!this.state.allLisRisCode)
            return [];

        const selectedCodes = billCodes
            .map(code => this.state.allLisRisCode.firstOrDefault(bill => bill.Code === code.Number && bill.CostCenterId == code.CostCenterId))
            .filter(code => code);

        return selectedCodes.map((c, idx) => {
            const price: number = +c.Price;
            return (
                <div
                    className="flex-container"
                    key={"code" + idx}
                    style={{ justifyContent: "flex-start", marginLeft: "15px" }}
                >
                    <div>
                        <span style={{ color: "#0070d2" }}>{`- ${c.Description}  (${c.Code}) ${price.toFixed(2)} $ `}</span>
                    </div>
                    <div style={{ marginLeft: "5px" }}>
                        <span
                            onClick={() => this.onBillCodeRemove(idx)}
                            className="open-schedule-redx pointer"
                        >
                            X
                        </span>
                    </div>
                </div>
            );
        });
    }

    private onBillCodeAdded(model: LISRISCode): void {
        const billCode = LabRequestBillCode.fromLisRisCode(model);

        const labRequest = this.props.model as LabRequest;
        const existingBillCodes = [...(labRequest?.billCodes ?? [])];
        existingBillCodes.push(billCode);
        this.props.onBillCodesChange && this.props.onBillCodesChange(existingBillCodes);
    }

    private onBillCodeRemove(index: number): void {
        const labRequest = this.props.model as LabRequest;
        const existingBillCodes = [...(labRequest?.billCodes ?? [])];
        existingBillCodes.splice(index, 1);
        this.props.onBillCodesChange && this.props.onBillCodesChange(existingBillCodes);
    }

    private handleOnSpeciatyChanged(value: string): void {
        this.props.onSpecialityChange && this.props.onSpecialityChange(value);
    }

    private get isConsultationRequest(): boolean {
        return this.props.model instanceof ConsultationRequest;
    }

    private get isLabRequest(): boolean {
        return this.props.model instanceof LabRequest;
    }

    private get hasLabRequestType(): boolean {
        const labRequest = this.props.model as LabRequest;
        return labRequest.type !== LabRequestType.None;
    }

    private get employeeList(): Immutable.List<SearchableItemModelT<Person>> {
        if (cacheStore.IsChristus() && this.isLabRequest) {
            return cacheStore.EmployeeCache
                .filter(p => !IsNullOrWhiteSpace(p.LicenceDSQ) && Employee.getIssuingOrgRule(p))
                .map(p => {
                    const person = Person.FromEmployee(p);
                    return new SearchableItemModelT<Person>(
                        `${person.NameWithPrefix}${IsNullOrWhiteSpace(p.LicenceDSQ) ? "" : ` (${p.LicenceDSQ})`}`,
                        person.ContactId,
                        person
                    );
                })
                .toImmutable();
        }

        return cacheStore.EmployeeCache
            .filter(p => !Employee.shouldHaveSupervisorBasedOnTitle(p))
            .map(p => {
                const person = Person.FromEmployee(p, true, true);
                return new SearchableItemModelT<Person>(
                    `${person.NameWithPrefix}${IsNullOrWhiteSpace(person.Licence) ? "" : ` (${person.Licence})`}`,
                    person.ContactId,
                    person
                );
            })
            .toImmutable();
    }

    private getFilteredLisRisCacheList(type: LabRequestType, costCenterId?: string): Immutable.List<SearchableItemModelT<LISRISCode>> {
        let currentLisRisList = LISRISCode.GetCodesByRequestType(type, this.props.siteId);

        if (costCenterId)
            currentLisRisList = currentLisRisList.filter(x => x.CostCenterId === costCenterId);

        return currentLisRisList
            .map(c => {
                let description = costCenterId
                    ? c.Description
                    : `${cacheStore.GetCostCenterNameById(c.CostCenterId).substring(0, 3)}. - ${c.Description}`;

                return new SearchableItemModelT<LISRISCode>(description, c.Code, c);
            })
            .toImmutable();
    }

    private getCostCenterOptions(type: LabRequestType, siteId: string): Immutable.List<ComboBoxDropDownItem> {
        if (!type || !siteId)
            return [].toImmutable();

        return LISRISCode
            .GetCostCenterByRequestType(type, siteId)
            .map((costCenter, index) => new ComboBoxDropDownItem(costCenter.ID, costCenter.Name, index))
            .toImmutable();
    }

    private isValid(field: string): boolean {
        return this.props.disabled || !!field;
    }

    private renderDueDate(dueDate: Date, dueDateText: string): JSX.Element {
        return (
            <div className="row">
                <div className="col-xs-6">
                    <TextboxControlWithDatePicker
                        id="labformRequestDueDate"
                        expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                        placeholder="YYYY-MM-DD"
                        textValue={dueDateText}
                        dateValue={dueDate}
                        onChange={(date: DateTimeWithPrecision) => this.props.onDueDateChange && this.props.onDueDateChange(date)}
                        fullWidth={true}
                    />
                </div>
                {!this.props.disabled && (
                    <div className="col-xs-6" style={{ paddingTop: "8px" }}>
                        <DateQuickLinkAddControl
                            id="labrequest-date-control"
                            value={dueDate}
                            onClick={(date: DateTimeWithPrecision) => this.props.onDueDateChange && this.props.onDueDateChange(date)}
                            numbersToRender={[
                                { value: 1, dateLevel: "week" },
                                { value: 2, dateLevel: "week" },
                                { value: 3, dateLevel: "week" },
                                { value: 1, dateLevel: "month" },
                                { value: 2, dateLevel: "month" },
                                { value: 3, dateLevel: "month" }
                            ]}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export { IMedicalRequestFormComponentProps, MedicalRequestFormComponent };
